import React from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { 
  LayoutDashboard, 
  BarChart2, 
  Settings, 
  MessageSquare,
  Brain,
  Users,
  DropletIcon,
  ChevronLeft,
  ChevronRight,
  CloudRain,
  LineChart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { useMode } from '@/contexts/ModeContext';
import useTranslation from '@/hooks/use-translation';
import { ScrollArea } from '@/components/ui/scroll-area';

interface SidebarProps {
  className?: string;
  collapsed?: boolean;
  setCollapsed?: (collapsed: boolean) => void;
  id?: string;
}

const Sidebar = ({ className, collapsed = false, setCollapsed }: SidebarProps) => {
  const { isRTL } = useLanguage();
  const { translate } = useTranslation();
  const { isAgriculture } = useMode();

  const toggleSidebar = () => {
    if (setCollapsed) {
      setCollapsed(!collapsed);
    }
  };

  const menuItems = [
    {
      icon: <LayoutDashboard size={20} />,
      label: translate("Dashboard"),
      to: "/",
    },
    {
      icon: <BarChart2 size={20} />,
      label: translate("Analytics"),
      to: "/analytics",
    },
    {
      icon: <CloudRain size={20} />,
      label: translate("Weather"),
      to: "/weather",
    },
    {
      icon: <LineChart size={20} />,
      label: translate("Predictions"),
      to: "/predictions",
    },
    {
      icon: <Brain size={20} />,
      label: translate("AI Insights"),
      to: "/ai-insights",
    },
    {
      icon: <MessageSquare size={20} />,
      label: translate("AI Assistant"),
      to: "/chatbot",
    },
    isAgriculture ? {
      icon: <Users size={20} />,
      label: translate("Community"),
      to: "/community",
    } : null,
    {
      icon: <Settings size={20} />,
      label: translate("Settings"),
      to: "/settings",
    },
  ].filter(Boolean);

  return (
    <div className={cn(
      "flex flex-col h-full border-r",
      className
    )}>
      {/* Logo Section */}
      <div className={cn(
        "flex items-center h-16 px-4 border-b",
        collapsed ? "justify-center" : "justify-between"
      )}>
        {!collapsed && (
          <div className="flex items-center gap-2">
            <DropletIcon className="h-6 w-6 text-primary animate-float" />
            <span className="font-semibold tracking-tight">
              {translate("AQUAGENIUS")}
            </span>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="shrink-0"
        >
          {isRTL ? (
            collapsed ? <ChevronLeft size={18} /> : <ChevronRight size={18} />
          ) : (
            collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />
          )}
        </Button>
      </div>

      {/* Navigation Links */}
      <ScrollArea className="flex-1 py-2">
        <nav className="grid gap-1 px-2">
          {menuItems.map((item, index) => item && (
            <NavLink
              key={index}
              to={item.to}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                "hover:bg-accent hover:text-accent-foreground",
                isActive ? 
                  "bg-primary/10 text-primary font-medium" : 
                  "text-muted-foreground",
                collapsed && "justify-center"
              )}
            >
              {item.icon}
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>
      </ScrollArea>
    </div>
  );
};

export default Sidebar;
